import React, { useState, useEffect } from 'react';
import api from '../services/api';

const GlobalControlPanel = ({ devices, sites, user, onClose, viewPreferences = {} }) => {
  const [selectedMagnitudes, setSelectedMagnitudes] = useState({});
  const [groupedData, setGroupedData] = useState([]);
  const [panelSize, setPanelSize] = useState('normal');
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [sessionId] = useState(() => Date.now()); // Identificador único por sesión

  useEffect(() => {
    console.log('🌐 GlobalControlPanel: Inicializando sesión', sessionId);
    // Agrupar dispositivos por sitio CON FILTRADO POR PERMISOS
    const groupedArray = [];

    // Obtener IDs de dispositivos permitidos para el usuario
    const getAllowedDeviceIds = () => {
      if (!user || user.role === 'admin') {
        // Admin puede ver todos los dispositivos
        return devices.map(d => d._id);
      }

      if (!user.devicePermissions) {
        return [];
      }

      return Object.entries(user.devicePermissions)
        .filter(([_, perm]) => perm && perm.allowed)
        .map(([deviceId, _]) => deviceId);
    };

    const allowedDeviceIds = getAllowedDeviceIds();

    // Ordenar sitios: los que tienen dispositivos online primero
    const sortedSites = [...sites].sort((a, b) => {
      const aDevices = devices.filter(d => d.site && (d.site._id === a._id || d.site === a._id));
      const bDevices = devices.filter(d => d.site && (d.site._id === b._id || d.site === b._id));
      const aOnline = aDevices.filter(d => d.status === 'online').length;
      const bOnline = bDevices.filter(d => d.status === 'online').length;
      if (bOnline !== aOnline) return bOnline - aOnline;
      return a.name.localeCompare(b.name);
    });

    sortedSites.forEach(site => {
      // Filtrar dispositivos del sitio que el usuario puede ver
      const siteDevices = devices.filter(device => {
        const belongsToSite = device.site && (device.site._id === site._id || device.site === site._id);
        const isAllowed = allowedDeviceIds.includes(device._id);
        return belongsToSite && isAllowed;
      });

      // Solo incluir el sitio si tiene dispositivos permitidos
      if (siteDevices.length > 0) {
        groupedArray.push({
          site: site,
          devices: siteDevices
        });
      }
    });

    setGroupedData(groupedArray);
    
    // Solo inicializar magnitudes para nuevos dispositivos, preservar selecciones existentes
    setSelectedMagnitudes(prevSelected => {
      // CREAR COPIA PROFUNDA del estado previo para evitar mutaciones
      const updatedMagnitudes = JSON.parse(JSON.stringify(prevSelected));
      
      groupedArray.forEach(group => {
        group.devices.forEach(device => {
          // Solo inicializar si el dispositivo no tiene selecciones previas
          if (!updatedMagnitudes[device._id] && device.magnitudes && device.magnitudes.length > 0) {
            console.log(`🔧 GlobalControlPanel: Initializing magnitudes for device ${device.name} (${device._id})`);
            // FILTRAR DUPLICADOS y por showInDashboard antes de inicializar
            const uniqueMagnitudes = device.magnitudes.filter((mag, index, arr) =>
              mag.showInDashboard !== false &&
              arr.findIndex(m => m.name === mag.name) === index
            );
            // CREAR COPIA INDEPENDIENTE de los nombres de magnitudes
            // INCLUIR booleanas aunque no tengan currentValue explícito
            let availableMagnitudesObjs = uniqueMagnitudes.filter(mag =>
              (mag.currentValue !== null && mag.currentValue !== undefined) ||
              mag.valueType === 'boolean'
            );

            // FILTRAR POR PERMISOS DE USUARIO
            if (user && user.role !== 'admin') {
              // Para usuarios no admin, filtrar según devicePermissions
              if (user.devicePermissions && user.devicePermissions[device._id]) {
                const devicePermissions = user.devicePermissions[device._id];
                if (devicePermissions.magnitudes) {
                  availableMagnitudesObjs = availableMagnitudesObjs.filter(magnitude => {
                    return devicePermissions.magnitudes.includes(magnitude.name);
                  });
                } else {
                  availableMagnitudesObjs = [];
                }
              } else {
                // Si no hay permisos para este dispositivo, no mostrar ninguna
                availableMagnitudesObjs = [];
              }
            }

            // FILTRAR POR PREFERENCIAS DE VISUALIZACIÓN DEL USUARIO
            const userViewPrefs = viewPreferences[device._id];
            if (userViewPrefs !== undefined && Array.isArray(userViewPrefs)) {
              if (userViewPrefs.length === 0) {
                // Si el array está vacío, no mostrar ninguna magnitud
                availableMagnitudesObjs = [];
              } else {
                // Filtrar solo las magnitudes que el usuario quiere ver
                availableMagnitudesObjs = availableMagnitudesObjs.filter(magnitude =>
                  userViewPrefs.includes(magnitude.name)
                );
              }
            }

            const availableMagnitudes = availableMagnitudesObjs.map(mag => String(mag.name)); // Asegurar que sean strings independientes
            
            updatedMagnitudes[device._id] = [...availableMagnitudes]; // Copia del array
            console.log(`📊 Initialized magnitudes for ${device.name}:`, updatedMagnitudes[device._id]);
          }
        });
      });
      
      // Limpiar selecciones de dispositivos que ya no existen
      const existingDeviceIds = groupedArray
        .flatMap(group => group.devices.map(device => device._id));
      
      Object.keys(updatedMagnitudes).forEach(deviceId => {
        if (!existingDeviceIds.includes(deviceId)) {
          delete updatedMagnitudes[deviceId];
        }
      });
      
      return updatedMagnitudes;
    });
    
    setLastUpdate(new Date());
  }, [devices, sites, user, viewPreferences]);

  const getDeviceValue = (device, magnitudeName) => {
    if (!magnitudeName) return null;
    
    const magnitude = device.magnitudes?.find(m => m.name === magnitudeName);
    if (!magnitude) return null;

    let value = null;
    
    // PRIORIDAD 1: Usar currentValue si existe (incluyendo false para booleanas)
    if (magnitude.currentValue !== null && magnitude.currentValue !== undefined) {
      value = magnitude.currentValue;
      console.log(`🔍 GlobalControlPanel: Using currentValue for ${magnitude.label}:`, value);
    } 
    // PRIORIDAD 2: Buscar en currentState si existe
    else if (device.currentState) {
      // Buscar el valor según el jsonPath
      if (magnitude.jsonPath) {
        const path = magnitude.jsonPath.split('.');
        let current = device.currentState;
        
        for (const key of path) {
          if (current !== null && current !== undefined && current[key] !== undefined) {
            current = current[key];
          } else {
            current = null;
            break;
          }
        }
        value = current;
      } else {
        // Si no hay jsonPath, buscar por nombre de magnitud
        value = device.currentState[magnitudeName] || device.currentState.value;
      }
    }
    // PRIORIDAD 3: Para magnitudes booleanas, asumir false como default
    else if (magnitude.valueType === 'boolean') {
      value = false; // Default para booleanas sin valor
      console.log(`🔘 GlobalControlPanel: Using default false for boolean ${magnitude.label}`);
    }

    // Normalizar valores booleanos string a boolean
    if (magnitude.valueType === 'boolean' && value !== null && value !== undefined) {
      if (typeof value === 'string') {
        const lowerValue = value.toLowerCase();
        if (lowerValue === 'true' || lowerValue === '1' || lowerValue === 'on' || lowerValue === 'yes') {
          value = true;
        } else if (lowerValue === 'false' || lowerValue === '0' || lowerValue === 'off' || lowerValue === 'no') {
          value = false;
        }
      }
      console.log(`🔘 GlobalControlPanel Boolean value for ${magnitude.label}:`, {
        original: magnitude.currentValue || 'none',
        currentState: device.currentState?.[magnitudeName] || 'none',
        normalized: value,
        type: typeof value
      });
    }

    return value;
  };

  const getValueColor = (device, magnitudeName, value) => {
    if (value === null || value === undefined) return '#6b7280';
    
    const magnitude = device.magnitudes?.find(m => m.name === magnitudeName);
    if (!magnitude) return '#6b7280';

    // Para magnitudes booleanas, usar colores configurados
    if (magnitude.valueType === 'boolean') {
      const isNormalState = magnitude.normalState === 'true' ? value : !value;
      return isNormalState ? (magnitude.normalColor || '#10b981') : (magnitude.abnormalColor || '#ef4444');
    }

    // Para magnitudes numéricas, usar lógica de rangos
    const min = magnitude.minNormalValue;
    const max = magnitude.maxNormalValue;
    
    if (min !== null && max !== null) {
      if (value < min || value > max) return '#ef4444'; // Rojo fuera de rango
      return '#10b981'; // Verde en rango normal
    }
    
    return '#3b82f6'; // Azul por defecto
  };

  const formatValue = (value, device, magnitudeName) => {
    if (value === null || value === undefined) return '-';
    
    const magnitude = device.magnitudes?.find(m => m.name === magnitudeName);
    if (!magnitude) return value.toString();
    
    // Para magnitudes booleanas, mostrar ON/OFF
    if (magnitude.valueType === 'boolean') {
      return value ? 'ON' : 'OFF';
    }
    
    if (typeof value === 'number') {
      const decimals = magnitude.decimals || 1;
      return value.toFixed(decimals);
    }
    
    return value.toString();
  };

  const handleMagnitudeChange = (deviceId, magnitudeName, isChecked) => {
    console.log(`🎛️ GlobalControlPanel [${sessionId}]: Cambiando magnitud`, { deviceId, magnitudeName, isChecked });
    
    setSelectedMagnitudes(prev => {
      // CREAR COPIA PROFUNDA del estado completo para evitar mutaciones
      const newState = JSON.parse(JSON.stringify(prev));
      const currentMagnitudes = newState[deviceId] || [];
      
      // LOG ESPECÍFICO PARA DEBUGGING BOOLEAN/ANALOG ISSUE
      console.log(`🔍 BEFORE CHANGE - Device ${deviceId}:`);
      console.log(`   - Target magnitude: ${magnitudeName} (${isChecked ? 'checking' : 'unchecking'})`);
      console.log(`   - Current magnitudes:`, currentMagnitudes);
      console.log(`   - Full state:`, prev);
      
      if (isChecked) {
        // Agregar magnitud si no está presente
        if (!currentMagnitudes.includes(magnitudeName)) {
          newState[deviceId] = [...currentMagnitudes, String(magnitudeName)];
        }
      } else {
        // Remover magnitud (crear nuevo array sin la magnitud)
        newState[deviceId] = currentMagnitudes.filter(mag => mag !== magnitudeName);
      }
      
      // LOG ESPECÍFICO DESPUÉS DEL CAMBIO
      console.log(`🔍 AFTER CHANGE - Device ${deviceId}:`);
      console.log(`   - New magnitudes:`, newState[deviceId]);
      console.log(`   - Full new state:`, newState);
      
      // VERIFICAR SI OTROS DEVICES FUERON AFECTADOS
      Object.keys(prev).forEach(otherDeviceId => {
        if (otherDeviceId !== deviceId) {
          const oldMags = prev[otherDeviceId] || [];
          const newMags = newState[otherDeviceId] || [];
          if (JSON.stringify(oldMags) !== JSON.stringify(newMags)) {
            console.log(`🚨 WARNING: Device ${otherDeviceId} was accidentally modified!`);
            console.log(`   - Old:`, oldMags);
            console.log(`   - New:`, newMags);
          }
        }
      });
      
      return newState;
    });
  };

  // Configuraciones de tamaño optimizadas para muchas magnitudes
  const getSizeConfig = () => {
    // Contar total de magnitudes seleccionadas
    const totalMagnitudes = Object.values(selectedMagnitudes).reduce((total, mags) => total + mags.length, 0);
    
    const configs = {
      small: {
        width: totalMagnitudes > 8 ? '85vw' : '70vw',
        maxWidth: totalMagnitudes > 8 ? '1400px' : '1000px',
        gaugeSize: totalMagnitudes > 12 ? 120 : totalMagnitudes > 8 ? 130 : 140,
        gaugeHeight: totalMagnitudes > 12 ? 80 : totalMagnitudes > 8 ? 90 : 100,
        gridColumns: totalMagnitudes > 12 ? 'repeat(auto-fit, minmax(140px, 1fr))' : 
                     totalMagnitudes > 8 ? 'repeat(auto-fit, minmax(160px, 1fr))' : 
                     'repeat(auto-fit, minmax(180px, 1fr))'
      },
      normal: {
        width: totalMagnitudes > 8 ? '90vw' : '80vw',
        maxWidth: totalMagnitudes > 8 ? '1800px' : '1400px',
        gaugeSize: totalMagnitudes > 12 ? 140 : totalMagnitudes > 8 ? 155 : 170,
        gaugeHeight: totalMagnitudes > 12 ? 100 : totalMagnitudes > 8 ? 110 : 120,
        gridColumns: totalMagnitudes > 12 ? 'repeat(auto-fit, minmax(160px, 1fr))' : 
                     totalMagnitudes > 8 ? 'repeat(auto-fit, minmax(180px, 1fr))' : 
                     'repeat(auto-fit, minmax(200px, 1fr))'
      },
      large: {
        width: totalMagnitudes > 8 ? '95vw' : '90vw',
        maxWidth: totalMagnitudes > 8 ? '2200px' : '1800px',
        gaugeSize: totalMagnitudes > 12 ? 160 : totalMagnitudes > 8 ? 175 : 190,
        gaugeHeight: totalMagnitudes > 12 ? 120 : totalMagnitudes > 8 ? 130 : 140,
        gridColumns: totalMagnitudes > 12 ? 'repeat(auto-fit, minmax(180px, 1fr))' : 
                     totalMagnitudes > 8 ? 'repeat(auto-fit, minmax(200px, 1fr))' : 
                     'repeat(auto-fit, minmax(220px, 1fr))'
      }
    };
    return configs[panelSize];
  };

  const sizeConfig = getSizeConfig();

  // Calcular rango automático para cada magnitud (igual que en GaugePanel)
  const calculateRange = (magnitude, value) => {
    let min, max, center;

    if (magnitude.minNormalValue !== null && magnitude.maxNormalValue !== null) {
      center = (magnitude.minNormalValue + magnitude.maxNormalValue) / 2;
      const normalRange = magnitude.maxNormalValue - magnitude.minNormalValue;
      const buffer = normalRange * 0.3;
      
      const halfRange = (normalRange / 2) + buffer;
      min = center - halfRange;
      max = center + halfRange;
    } else {
      const absValue = Math.abs(value);
      if (absValue === 0) {
        min = -10;
        max = 10;
        center = 0;
      } else {
        const range = absValue * 1.5;
        center = value;
        min = center - range;
        max = center + range;
      }
    }

    if (value < min) {
      const adjustment = min - value + Math.abs(value * 0.1);
      min -= adjustment;
      max -= adjustment;
    }
    if (value > max) {
      const adjustment = value - max + Math.abs(value * 0.1);
      min += adjustment;
      max += adjustment;
    }

    return { min, max, center };
  };

  // Componente de gauge individual que maneja valores numéricos y booleanos
  const Gauge = ({ device, magnitude }) => {
    const rawValue = getDeviceValue(device, magnitude.name);
    
    // Manejar valores booleanos
    if (magnitude.valueType === 'boolean') {
      const totalMagnitudes = Object.values(selectedMagnitudes).reduce((total, mags) => total + mags.length, 0);
      
      return (
        <div style={{
          background: 'linear-gradient(145deg, #1a1a2e, #16213e)',
          borderRadius: '12px',
          padding: totalMagnitudes > 12 ? '0.75rem' : totalMagnitudes > 8 ? '1rem' : '1.25rem',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          minHeight: totalMagnitudes > 12 ? '160px' : totalMagnitudes > 8 ? '180px' : '220px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: totalMagnitudes > 8 ? '0.5rem' : '0.75rem',
            marginBottom: totalMagnitudes > 8 ? '0.75rem' : '1.5rem'
          }}>
            <div style={{
              background: (() => {
                const isNormalState = magnitude.normalState === 'true' ? rawValue : !rawValue;
                const primaryColor = isNormalState ? (magnitude.normalColor || '#10b981') : (magnitude.abnormalColor || '#ef4444');
                const secondaryColor = isNormalState 
                  ? (magnitude.normalColor ? magnitude.normalColor.replace('#', '#').slice(0, 7) + 'cc' : '#059669') 
                  : (magnitude.abnormalColor ? magnitude.abnormalColor.replace('#', '#').slice(0, 7) + 'cc' : '#dc2626');
                return `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`;
              })(),
              borderRadius: '12px',
              padding: '0.5rem',
              boxShadow: (() => {
                const isNormalState = magnitude.normalState === 'true' ? rawValue : !rawValue;
                const shadowColor = isNormalState ? (magnitude.normalColor || '#10b981') : (magnitude.abnormalColor || '#ef4444');
                return `0 4px 12px ${shadowColor}50`;
              })(),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <span style={{ fontSize: totalMagnitudes > 8 ? '1.2rem' : '1.5rem' }}>
                {magnitude.icon || '🔘'}
              </span>
            </div>
            <h3 style={{
              margin: 0,
              fontSize: '1rem',
              fontWeight: '700',
              color: '#ffffff',
              textAlign: 'center'
            }}>
              {magnitude.label}
            </h3>
          </div>
          
          {/* Estado ON/OFF con colores configurados */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: totalMagnitudes > 12 ? '50px' : totalMagnitudes > 8 ? '60px' : '70px',
            height: totalMagnitudes > 12 ? '50px' : totalMagnitudes > 8 ? '60px' : '70px',
            borderRadius: '50%',
            background: (() => {
              const isNormalState = magnitude.normalState === 'true' ? rawValue : !rawValue;
              const primaryColor = isNormalState ? (magnitude.normalColor || '#10b981') : (magnitude.abnormalColor || '#ef4444');
              const secondaryColor = isNormalState 
                ? (magnitude.normalColor ? magnitude.normalColor + '99' : '#059669') 
                : (magnitude.abnormalColor ? magnitude.abnormalColor + '99' : '#dc2626');
              return `radial-gradient(circle, ${primaryColor} 30%, ${secondaryColor} 100%)`;
            })(),
            boxShadow: (() => {
              const isNormalState = magnitude.normalState === 'true' ? rawValue : !rawValue;
              const shadowColor = isNormalState ? (magnitude.normalColor || '#10b981') : (magnitude.abnormalColor || '#ef4444');
              return `0 0 20px ${shadowColor}50, inset 0 2px 4px rgba(255, 255, 255, 0.2)`;
            })(),
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: 'white',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
            marginBottom: '1rem'
          }}>
            {rawValue ? 'ON' : 'OFF'}
          </div>
          
          {/* Estado simple compacto */}
          <div style={{
            textAlign: 'center',
            fontSize: '0.75rem',
            color: 'rgba(255, 255, 255, 0.7)',
            marginTop: '0.5rem'
          }}>
            <span style={{ 
              color: (() => {
                const isNormalState = magnitude.normalState === 'true' ? rawValue : !rawValue;
                return isNormalState ? (magnitude.normalColor || '#10b981') : (magnitude.abnormalColor || '#ef4444');
              })(), 
              fontWeight: 'bold' 
            }}>
              {rawValue ? 'ACTIVO' : 'INACTIVO'}
            </span>
          </div>
        </div>
      );
    }
    
    // Para valores numéricos, continuar con el comportamiento original
    const value = parseFloat(rawValue);
    if (isNaN(value)) return null;

    const { min, max, center } = calculateRange(magnitude, value);
    const range = max - min;
    
    // Calcular ángulo de la aguja (igual que en GaugePanel)
    let angle;
    if (magnitude.minNormalValue !== null && magnitude.maxNormalValue !== null) {
      // Rango de -135° a +45° (total 180°) con el centro en -45° (arriba)
      const percentage = Math.min(Math.max((value - min) / range, 0), 1);
      angle = -135 + (percentage * 180);
    } else {
      // Comportamiento original: de -90° a +90°
      const percentage = Math.min(Math.max((value - min) / range, 0), 1);
      angle = -90 + (percentage * 180);
    }
    
    // Determinar color según rango normal
    let needleColor = '#3b82f6'; // Azul por defecto
    if (magnitude.minNormalValue !== null && magnitude.maxNormalValue !== null) {
      if (value < magnitude.minNormalValue || value > magnitude.maxNormalValue) {
        needleColor = '#ef4444'; // Rojo fuera de rango
      } else {
        needleColor = '#10b981'; // Verde en rango normal
      }
    }

    // Generar marcas en el gauge (igual que en GaugePanel)
    const generateTicks = () => {
      const ticks = [];
      const tickCount = 5; // Número de marcas principales
      
      for (let i = 0; i <= tickCount; i++) {
        const tickValue = min + (range * i / tickCount);
        
        // Calcular ángulo según el tipo de gauge
        let tickAngle;
        if (magnitude.minNormalValue !== null && magnitude.maxNormalValue !== null) {
          // Gauge con rango normal: de -135° a +45°
          tickAngle = -135 + (i * 180 / tickCount);
        } else {
          // Gauge estándar: de -90° a +90°
          tickAngle = -90 + (i * 180 / tickCount);
        }
        
        const isInNormalRange = magnitude.minNormalValue !== null && 
                               magnitude.maxNormalValue !== null &&
                               tickValue >= magnitude.minNormalValue && 
                               tickValue <= magnitude.maxNormalValue;
        
        ticks.push({
          angle: tickAngle,
          value: tickValue,
          isNormal: isInNormalRange
        });
      }
      return ticks;
    };

    const ticks = generateTicks();

    const centerX = sizeConfig.gaugeSize / 2;
    const centerY = sizeConfig.gaugeHeight / 2;
    const radius = Math.min(sizeConfig.gaugeSize, sizeConfig.gaugeHeight) * 0.35;
    
    const totalMagnitudes = Object.values(selectedMagnitudes).reduce((total, mags) => total + mags.length, 0);
    
    return (
      <div style={{
        background: 'linear-gradient(145deg, #1a1a2e, #16213e)',
        borderRadius: '12px',
        padding: totalMagnitudes > 12 ? '0.75rem' : totalMagnitudes > 8 ? '1rem' : '1.25rem',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        minHeight: totalMagnitudes > 12 ? '180px' : totalMagnitudes > 8 ? '200px' : '240px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Efecto de brillo de fondo */}
        <div style={{
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          width: '200%',
          height: '200%',
          background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)',
          animation: 'pulse 4s ease-in-out infinite',
          zIndex: 0
        }}></div>

        {/* Header con icono y título */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: totalMagnitudes > 8 ? '0.5rem' : '0.75rem',
          marginBottom: totalMagnitudes > 8 ? '0.75rem' : '1rem',
          position: 'relative',
          zIndex: 1
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
            borderRadius: totalMagnitudes > 8 ? '8px' : '12px',
            padding: totalMagnitudes > 8 ? '0.375rem' : '0.5rem',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <span style={{ 
              fontSize: totalMagnitudes > 8 ? '1.2rem' : '1.5rem',
              filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
            }}>
              {magnitude.icon || '📊'}
            </span>
          </div>
          <h3 style={{
            margin: 0,
            fontSize: totalMagnitudes > 12 ? '0.95rem' : totalMagnitudes > 8 ? '1.05rem' : '1.2rem',
            fontWeight: '700',
            color: '#ffffff',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
            letterSpacing: '0.025em'
          }}>
            {magnitude.label}
          </h3>
        </div>

        {/* Gauge SVG - Igual al de GaugePanel */}
        <div style={{ 
          position: 'relative', 
          width: `${sizeConfig.gaugeSize}px`, 
          height: `${sizeConfig.gaugeHeight}px`, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          background: 'radial-gradient(circle, rgba(255, 255, 255, 0.05) 0%, transparent 60%)',
          borderRadius: '50%',
          boxShadow: 'inset 0 4px 8px rgba(0, 0, 0, 0.3)',
          zIndex: 1
        }}>
          <svg 
            width={sizeConfig.gaugeSize} 
            height={sizeConfig.gaugeHeight} 
            viewBox={`0 0 ${sizeConfig.gaugeSize} ${sizeConfig.gaugeHeight}`}
          >
            {/* Arco principal del gauge */}
            {(() => {
              // Calcular el arco según el tipo de gauge
              if (magnitude.minNormalValue !== null && magnitude.maxNormalValue !== null) {
                // Arco de -135° a +45° (con centro arriba)
                const startX = centerX + radius * Math.cos(-135 * Math.PI / 180);
                const startY = centerY + radius * Math.sin(-135 * Math.PI / 180);
                const endX = centerX + radius * Math.cos(45 * Math.PI / 180);
                const endY = centerY + radius * Math.sin(45 * Math.PI / 180);
                
                return (
                  <path
                    d={`M ${startX} ${startY} A ${radius} ${radius} 0 1 1 ${endX} ${endY}`}
                    fill="none"
                    stroke="rgba(255, 255, 255, 0.2)"
                    strokeWidth="10"
                    strokeLinecap="round"
                    filter="drop-shadow(0 0 4px rgba(255, 255, 255, 0.1))"
                  />
                );
              } else {
                // Arco estándar de -90° a +90°
                return (
                  <path
                    d={`M ${centerX - radius} ${centerY} A ${radius} ${radius} 0 0 1 ${centerX + radius} ${centerY}`}
                    fill="none"
                    stroke="rgba(255, 255, 255, 0.2)"
                    strokeWidth="10"
                    strokeLinecap="round"
                    filter="drop-shadow(0 0 4px rgba(255, 255, 255, 0.1))"
                  />
                );
              }
            })()}
            
            {/* Rangos de colores usando múltiples segmentos pequeños */}
            {magnitude.minNormalValue !== null && magnitude.maxNormalValue !== null && (
              <>
                {/* Generar segmentos uniformes para el gauge */}
                {Array.from({length: 60}, (_, i) => {
                  // Adaptar ángulos según tipo de gauge
                  let segmentAngle, nextSegmentAngle;
                  if (magnitude.minNormalValue !== null && magnitude.maxNormalValue !== null) {
                    // Gauge con centro arriba: -135° a +45°
                    segmentAngle = -135 + (i * 3); // Cada segmento ocupa 3 grados
                    nextSegmentAngle = segmentAngle + 3;
                  } else {
                    // Gauge estándar: -90° a +90°
                    segmentAngle = -90 + (i * 3);
                    nextSegmentAngle = segmentAngle + 3;
                  }
                  
                  const segmentPercent = i / 60;
                  const segmentValue = min + (range * segmentPercent);
                  
                  // Determinar color del segmento con efectos brillantes
                  let segmentColor = 'rgba(255, 255, 255, 0.1)'; // Gris transparente por defecto
                  if (segmentValue >= magnitude.minNormalValue && segmentValue <= magnitude.maxNormalValue) {
                    segmentColor = '#00ff88'; // Verde neón para rango normal
                  } else {
                    segmentColor = '#ff3366'; // Rojo neón para fuera del rango
                  }
                  
                  const startX = centerX + radius * Math.cos(segmentAngle * Math.PI / 180);
                  const startY = centerY + radius * Math.sin(segmentAngle * Math.PI / 180);
                  const endX = centerX + radius * Math.cos(nextSegmentAngle * Math.PI / 180);
                  const endY = centerY + radius * Math.sin(nextSegmentAngle * Math.PI / 180);
                  
                  return (
                    <path
                      key={i}
                      d={`M ${startX} ${startY} A ${radius} ${radius} 0 0 1 ${endX} ${endY}`}
                      fill="none"
                      stroke={segmentColor}
                      strokeWidth="10"
                      opacity="0.9"
                      filter="drop-shadow(0 0 6px currentColor)"
                      style={{ filter: `drop-shadow(0 0 6px ${segmentColor})` }}
                    />
                  );
                })}
              </>
            )}
            
            {/* Marcas del gauge corregidas */}
            {ticks.map((tick, index) => {
              // Convertir ángulo a radianes para cálculos correctos
              const angleRad = (tick.angle * Math.PI) / 180;
              const x1 = centerX + (radius - 10) * Math.cos(angleRad);
              const y1 = centerY + (radius - 10) * Math.sin(angleRad);
              const x2 = centerX + radius * Math.cos(angleRad);
              const y2 = centerY + radius * Math.sin(angleRad);
              
              // Posición del texto
              const textX = centerX + (radius + 15) * Math.cos(angleRad);
              const textY = centerY + (radius + 15) * Math.sin(angleRad);
              
              return (
                <g key={index}>
                  <line
                    x1={x1} y1={y1} x2={x2} y2={y2}
                    stroke={tick.isNormal ? '#00ff88' : 'rgba(255, 255, 255, 0.6)'}
                    strokeWidth="3"
                    filter="drop-shadow(0 0 3px currentColor)"
                  />
                  <text
                    x={textX}
                    y={textY + 3}
                    textAnchor="middle"
                    fontSize="11"
                    fill="rgba(255, 255, 255, 0.9)"
                    fontWeight="600"
                    style={{ textShadow: '0 0 6px rgba(255, 255, 255, 0.5)' }}
                  >
                    {tick.value.toFixed(magnitude.decimals || 0)}
                  </text>
                </g>
              );
            })}
            
            {/* Aguja corregida con efectos */}
            <g>
              {/* Sombra de la aguja */}
              <line
                x1={centerX + 2}
                y1={centerY + 2}
                x2={centerX + (radius - 20) * Math.cos(angle * Math.PI / 180) + 2}
                y2={centerY + (radius - 20) * Math.sin(angle * Math.PI / 180) + 2}
                stroke="rgba(0, 0, 0, 0.3)"
                strokeWidth="4"
                strokeLinecap="round"
              />
              {/* Aguja principal */}
              <line
                x1={centerX}
                y1={centerY}
                x2={centerX + (radius - 20) * Math.cos(angle * Math.PI / 180)}
                y2={centerY + (radius - 20) * Math.sin(angle * Math.PI / 180)}
                stroke={needleColor}
                strokeWidth="4"
                strokeLinecap="round"
                filter="drop-shadow(0 0 8px currentColor)"
                style={{ filter: `drop-shadow(0 0 8px ${needleColor})` }}
              />
              {/* Centro de la aguja con efectos */}
              <circle
                cx={centerX + 1} 
                cy={centerY + 1} 
                r="6"
                fill="rgba(0, 0, 0, 0.3)"
              />
              <circle
                cx={centerX} 
                cy={centerY} 
                r="6"
                fill={needleColor}
                filter="drop-shadow(0 0 6px currentColor)"
                style={{ filter: `drop-shadow(0 0 6px ${needleColor})` }}
              />
              <circle
                cx={centerX} 
                cy={centerY} 
                r="3"
                fill="rgba(255, 255, 255, 0.3)"
              />
            </g>
            
            {/* Valor en la parte inferior con efectos */}
            <text
              x={centerX} 
              y={sizeConfig.gaugeHeight - 10}
              textAnchor="middle"
              fontSize={Math.max(14, sizeConfig.gaugeSize * 0.08)}
              fontWeight="bold"
              fill={needleColor}
              style={{ 
                filter: `drop-shadow(0 0 8px ${needleColor})`,
                textShadow: `0 0 10px ${needleColor}`
              }}
            >
              {value.toFixed(magnitude.decimals || 1)}{magnitude.unit || ''}
            </text>
          </svg>
        </div>

      </div>
    );
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        background: 'linear-gradient(145deg, #0f172a, #1e293b)',
        borderRadius: '20px',
        padding: Object.values(selectedMagnitudes).reduce((total, mags) => total + mags.length, 0) > 12 ? '1.25rem' : Object.values(selectedMagnitudes).reduce((total, mags) => total + mags.length, 0) > 8 ? '1.5rem' : '2rem',
        width: sizeConfig.width || '95%',
        maxWidth: sizeConfig.maxWidth || '1800px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        position: 'relative'
      }}>
        {/* Header del panel */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: Object.values(selectedMagnitudes).reduce((total, mags) => total + mags.length, 0) > 12 ? '1.25rem' : Object.values(selectedMagnitudes).reduce((total, mags) => total + mags.length, 0) > 8 ? '1.5rem' : '2rem',
          paddingBottom: Object.values(selectedMagnitudes).reduce((total, mags) => total + mags.length, 0) > 12 ? '1rem' : Object.values(selectedMagnitudes).reduce((total, mags) => total + mags.length, 0) > 8 ? '1.25rem' : '1.5rem',
          borderBottom: '2px solid rgba(255, 255, 255, 0.1)',
          position: 'relative',
          zIndex: 1
        }}>
          <div>
            <h2 style={{
              margin: 0,
              fontSize: '1.8rem',
              fontWeight: '800',
              color: '#ffffff',
              textShadow: '0 4px 8px rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <span style={{
                background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                borderRadius: '12px',
                padding: '0.5rem',
                fontSize: '1.5rem',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)'
              }}>
                🎛️
              </span>
              Panel de Control Global
            </h2>
            <p style={{
              margin: '0.5rem 0 0 0',
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '1rem',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)'
            }}>
              Monitoreando {Object.keys(groupedData).length} sitios con múltiples magnitudes
            </p>
          </div>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            {/* Control de tamaño */}
            <div style={{
              display: 'flex',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              padding: '0.5rem',
              gap: '0.5rem',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              {['small', 'normal', 'large'].map((size) => (
                <button
                  key={size}
                  onClick={() => setPanelSize(size)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    background: panelSize === size 
                      ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)' 
                      : 'transparent',
                    color: panelSize === size ? 'white' : 'rgba(255, 255, 255, 0.7)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    transition: 'all 0.2s ease',
                    boxShadow: panelSize === size 
                      ? '0 2px 8px rgba(59, 130, 246, 0.4)' 
                      : 'none'
                  }}
                >
                  {size === 'small' ? 'S' : size === 'normal' ? 'M' : 'L'}
                </button>
              ))}
            </div>
            
            <button
              onClick={onClose}
              style={{
                padding: '0.75rem',
                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '1.3rem',
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)',
                transition: 'all 0.2s ease'
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content por sitios */}
        <div style={{ display: 'grid', gap: '2rem' }}>
          {groupedData.map(({ site, devices: siteDevices }) => (
            <div key={site._id} style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '15px',
              padding: '1.5rem',
              border: '1px solid rgba(0, 255, 255, 0.2)'
            }}>
              {/* Site Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '1.5rem',
                borderBottom: '1px solid rgba(0, 255, 255, 0.2)',
                paddingBottom: '1rem'
              }}>
                <h3 style={{
                  margin: 0,
                  fontSize: '1.5rem',
                  color: '#00ffff',
                  textShadow: '0 0 5px rgba(0, 255, 255, 0.5)'
                }}>
                  🏢 {site.name}
                </h3>
                <div style={{
                  marginLeft: '1rem',
                  padding: '0.25rem 0.75rem',
                  background: 'rgba(16, 185, 129, 0.2)',
                  border: '1px solid #10b981',
                  borderRadius: '20px',
                  fontSize: '0.875rem',
                  color: '#10b981'
                }}>
                  {siteDevices.length} dispositivos
                </div>
              </div>

              {/* Devices con checkboxes y gauges */}
              {siteDevices.map(device => {
                const deviceMagnitudes = selectedMagnitudes[device._id] || [];
                // FILTRAR DUPLICADOS y por showInDashboard por nombre de magnitud
                const uniqueDeviceMagnitudes = device.magnitudes
                  ? device.magnitudes.filter((mag, index, arr) =>
                      mag.showInDashboard !== false &&
                      arr.findIndex(m => m.name === mag.name) === index
                    )
                  : [];
                // INCLUIR magnitudes booleanas siempre, incluso sin valor explícito
                let availableMagnitudes = uniqueDeviceMagnitudes.filter(mag => {
                  const value = getDeviceValue(device, mag.name);
                  // Incluir si tiene valor O si es booleana (pueden usar valor default)
                  return value !== null || mag.valueType === 'boolean';
                }) || [];

                // FILTRAR POR PERMISOS DE USUARIO
                if (user && user.role !== 'admin') {
                  // Para usuarios no admin, filtrar según devicePermissions
                  if (user.devicePermissions && user.devicePermissions[device._id]) {
                    const devicePermissions = user.devicePermissions[device._id];
                    if (devicePermissions.magnitudes) {
                      availableMagnitudes = availableMagnitudes.filter(magnitude => {
                        return devicePermissions.magnitudes.includes(magnitude.name);
                      });
                    } else {
                      availableMagnitudes = [];
                    }
                  } else {
                    // Si no hay magnitudes habilitadas configuradas, no mostrar ninguna
                    availableMagnitudes = [];
                  }
                }
                
                // Log específico para debuggear el problema
                if (device.magnitudes && device.magnitudes.length >= 4) {
                  console.log(`🎛️ GlobalControlPanel: Device ${device.name} (${device._id})`);
                  console.log(`📊 Selected magnitudes:`, deviceMagnitudes);
                  console.log(`📊 Available magnitudes:`, availableMagnitudes.map(m => `${m.name}(${m.valueType})`));
                  
                  // IDENTIFICAR LA ÚLTIMA ANALÓGICA Y LA BOOLEANA
                  const analogs = availableMagnitudes.filter(m => m.valueType === 'numeric');
                  const booleans = availableMagnitudes.filter(m => m.valueType === 'boolean');
                  const lastAnalog = analogs[analogs.length - 1];
                  const firstBoolean = booleans[0];
                  
                  if (lastAnalog && firstBoolean) {
                    console.log(`🔍 CRITICAL: Last Analog: ${lastAnalog.name}, Boolean: ${firstBoolean.name}`);
                    console.log(`   - Last Analog selected: ${deviceMagnitudes.includes(lastAnalog.name)}`);
                    console.log(`   - Boolean selected: ${deviceMagnitudes.includes(firstBoolean.name)}`);
                  }
                }

                // Colores según estado del dispositivo
                const isOnline = device.status === 'online';
                const deviceBorderColor = isOnline ? 'rgba(16, 185, 129, 0.6)' : 'rgba(239, 68, 68, 0.6)';
                const deviceBgGradient = isOnline
                  ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.05) 100%)'
                  : 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(239, 68, 68, 0.05) 100%)';

                return (
                  <div key={device._id} style={{
                    background: deviceBgGradient,
                    borderRadius: '12px',
                    padding: '1.5rem',
                    border: `2px solid ${deviceBorderColor}`,
                    marginBottom: '1.5rem',
                    boxShadow: `0 0 10px ${isOnline ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`
                  }}>
                    {/* Device Header con status */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '1rem',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                      paddingBottom: '1rem'
                    }}>
                      <h4 style={{
                        margin: 0,
                        fontSize: '1.2rem',
                        color: '#ffffff',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        📱 {device.name}
                      </h4>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <div style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: device.status === 'online' ? '#10b981' : '#ef4444',
                          boxShadow: `0 0 6px ${device.status === 'online' ? '#10b981' : '#ef4444'}`
                        }} />
                        <span style={{
                          fontSize: '0.8rem',
                          color: device.status === 'online' ? '#10b981' : '#ef4444'
                        }}>
                          {device.status === 'online' ? 'En línea' : 'Desconectado'}
                        </span>
                      </div>
                    </div>

                    {/* Checkboxes para seleccionar magnitudes */}
                    {availableMagnitudes.length > 0 && (
                      <div style={{
                        marginBottom: '1.5rem',
                        background: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '8px',
                        padding: '1rem'
                      }}>
                        <label style={{
                          display: 'block',
                          marginBottom: '0.75rem',
                          fontSize: '0.9rem',
                          color: '#00ffff',
                          fontWeight: '600'
                        }}>
                          📊 Magnitudes a mostrar:
                        </label>
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '0.75rem'
                        }}>
                          {availableMagnitudes.map(magnitude => (
                            <label key={`${device._id}-${magnitude.name}`} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              cursor: 'pointer',
                              padding: '0.5rem 0.75rem',
                              background: deviceMagnitudes.includes(magnitude.name)
                                ? 'rgba(16, 185, 129, 0.2)'
                                : 'rgba(255, 255, 255, 0.1)',
                              border: `1px solid ${deviceMagnitudes.includes(magnitude.name)
                                ? '#10b981'
                                : 'rgba(255, 255, 255, 0.2)'}`,
                              borderRadius: '6px',
                              fontSize: '0.8rem',
                              color: deviceMagnitudes.includes(magnitude.name)
                                ? '#10b981'
                                : 'rgba(255, 255, 255, 0.8)',
                              transition: 'all 0.2s ease'
                            }}>
                              <input
                                type="checkbox"
                                checked={(() => {
                                  const isChecked = deviceMagnitudes.includes(magnitude.name);
                                  // Log específico para debuggear checkboxes problemáticos
                                  if (device.magnitudes && device.magnitudes.length >= 4) {
                                    const isLastAnalog = magnitude.valueType === 'numeric' && 
                                      device.magnitudes.filter(m => m.valueType === 'numeric').pop()?.name === magnitude.name;
                                    const isBoolean = magnitude.valueType === 'boolean';
                                    
                                    if (isLastAnalog || isBoolean) {
                                      console.log(`🔘 CRITICAL CHECKBOX ${magnitude.label} (${magnitude.name}, ${magnitude.valueType}) for device ${device._id}:`);
                                      console.log(`   - Checked: ${isChecked}`);
                                      console.log(`   - Is Last Analog: ${isLastAnalog}`);
                                      console.log(`   - Is Boolean: ${isBoolean}`);
                                      console.log(`   - DeviceMagnitudes array:`, deviceMagnitudes);
                                    }
                                  }
                                  return isChecked;
                                })()}
                                onChange={(e) => {
                                  console.log(`🔘 Checkbox onChange: ${magnitude.label} (${magnitude.name}) for device ${device._id}: ${e.target.checked}`);
                                  handleMagnitudeChange(device._id, magnitude.name, e.target.checked);
                                }}
                                style={{
                                  accentColor: '#10b981'
                                }}
                              />
                              <span>{magnitude.icon || '📊'}</span>
                              <span>{magnitude.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Grid de Gauges para magnitudes seleccionadas */}
                    {deviceMagnitudes.length > 0 && (
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: sizeConfig.gridColumns,
                        gap: Object.values(selectedMagnitudes).reduce((total, mags) => total + mags.length, 0) > 12 ? '0.5rem' : Object.values(selectedMagnitudes).reduce((total, mags) => total + mags.length, 0) > 8 ? '0.75rem' : '1rem',
                        justifyItems: 'center'
                      }}>
                        {deviceMagnitudes.map(magnitudeName => {
                          const magnitude = device.magnitudes?.find(m => m.name === magnitudeName);
                          if (!magnitude) return null;
                          
                          return (
                            <Gauge 
                              key={`${device._id}-${magnitudeName}`} 
                              device={device} 
                              magnitude={magnitude} 
                            />
                          );
                        })}
                      </div>
                    )}

                    {/* Mensaje cuando no hay magnitudes seleccionadas */}
                    {deviceMagnitudes.length === 0 && availableMagnitudes.length > 0 && (
                      <div style={{
                        textAlign: 'center',
                        padding: '2rem',
                        color: 'rgba(255, 255, 255, 0.6)',
                        fontStyle: 'italic'
                      }}>
                        Selecciona las magnitudes que deseas visualizar
                      </div>
                    )}

                    {/* Última actualización */}
                    {device.lastSeen && (
                      <div style={{
                        marginTop: '1rem',
                        fontSize: '0.7rem',
                        color: '#6b7280',
                        textAlign: 'center',
                        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                        paddingTop: '0.5rem'
                      }}>
                        Última actualización: {new Date(device.lastSeen).toLocaleString()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>


        {Object.keys(groupedData).length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '3rem',
            color: '#6b7280'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🤖</div>
            <div style={{ fontSize: '1.25rem' }}>No hay dispositivos disponibles</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalControlPanel;